/**
 * ORCHID cart — quantity, removal and the order note, without a page reload.
 *
 * Every line is rendered server-side by Liquid; this only writes to Shopify's own Cart AJAX
 * API on the shop's own domain and then asks Shopify to re-render this section, so the prices
 * that land back on the page are the ones Liquid's money filters produced. Nothing here
 * formats a price except the optimistic figure shown for the few hundred milliseconds before
 * the real one arrives, and that goes through Intl.NumberFormat with the cart's own currency.
 *
 * No image is expressed as a data: URI anywhere in this file.
 */
(function () {
  'use strict';

  var SELECTOR = '[data-orchid-cart]';

  function formatMoney(cents, currency, locale) {
    var whole = cents % 100 === 0;
    var amount = cents / 100;
    try {
      return new Intl.NumberFormat(locale || undefined, {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: whole ? 0 : 2
      }).format(amount);
    } catch (error) {
      return '$' + (whole ? String(amount) : amount.toFixed(2));
    }
  }

  function fill(template, values) {
    return String(template || '').replace(/\[(\w+)\]/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
    });
  }

  function init(root) {
    if (!root || root.orchidCartReady) return;
    root.orchidCartReady = true;

    var d = root.dataset;
    var form = root.querySelector('.orchid-cart__form');
    if (!form) return;

    var linesEl = root.querySelector('[data-orchid-cart-lines]');
    var subtotalEl = root.querySelector('[data-orchid-cart-subtotal]');
    var statusEl = root.querySelector('[data-orchid-cart-status]');
    var errorEl = root.querySelector('[data-orchid-cart-error]');
    var noteEl = root.querySelector('[data-orchid-cart-note]');
    var noteStatusEl = root.querySelector('[data-orchid-cart-note-status]');

    var queue = {};
    var timers = {};
    var running = false;

    function money(cents) {
      return formatMoney(cents, d.currency, d.locale);
    }

    function say(message) {
      if (statusEl) statusEl.textContent = message || '';
    }

    function setError(message) {
      if (errorEl) errorEl.textContent = message || '';
    }

    function pendingWork() {
      for (var key in queue) {
        if (Object.prototype.hasOwnProperty.call(queue, key)) return true;
      }
      return false;
    }

    /* --- Optimistic figures ------------------------------------------ */

    // `data-quantity` on the row and `data-cents` on the subtotal are server truth: they are
    // written by Liquid and updated only from a response. The optimistic pass touches nothing
    // but the text a person is looking at, which is what makes the revert below exact rather
    // than a reconstruction. Cart-level discounts can leave the optimistic subtotal a few
    // cents out for one round trip; nothing acts on that number, and Liquid's replaces it.
    function serverQuantity(line) {
      var value = parseInt(line.getAttribute('data-quantity'), 10);
      return isNaN(value) ? 0 : value;
    }

    function serverCents() {
      if (!subtotalEl) return NaN;
      return parseInt(subtotalEl.getAttribute('data-cents'), 10);
    }

    function optimistic(line, quantity) {
      var unit = parseInt(line.getAttribute('data-unit-price'), 10) || 0;

      var totalEl = line.querySelector('[data-orchid-cart-line-total]');
      if (totalEl) totalEl.textContent = money(unit * quantity);

      var current = serverCents();
      if (subtotalEl && !isNaN(current)) {
        var next = current + unit * (quantity - serverQuantity(line));
        subtotalEl.textContent = money(next < 0 ? 0 : next);
      }
    }

    // At one, the minus button has nothing left to do and Remove is the next step. Disabling
    // the control a person is standing on would throw their focus to the top of the document,
    // so hand it to Remove first — which is where they were heading anyway.
    function setMinusState(line, quantity) {
      var minus = line.querySelector('[data-orchid-cart-step="-1"]');
      if (!minus) return;

      var off = quantity <= 1;
      if (off && document.activeElement === minus) {
        var removeButton = line.querySelector('[data-orchid-cart-remove]');
        if (removeButton) removeButton.focus();
      }
      minus.disabled = off;
    }

    // The write failed, so everything on screen is a guess. Put the last known-good numbers
    // back rather than reloading the page out from under the message explaining what happened.
    function revert(line) {
      var unit = parseInt(line.getAttribute('data-unit-price'), 10) || 0;
      var quantity = serverQuantity(line);

      var input = line.querySelector('[data-orchid-cart-qty]');
      if (input) input.value = String(quantity);

      var totalEl = line.querySelector('[data-orchid-cart-line-total]');
      if (totalEl) totalEl.textContent = money(unit * quantity);

      setMinusState(line, quantity);

      var current = serverCents();
      if (subtotalEl && !isNaN(current)) subtotalEl.textContent = money(current);

      line.classList.remove('is-leaving');
      line.removeAttribute('aria-busy');
    }

    /* --- Writing ------------------------------------------------------- */

    function schedule(line, quantity) {
      var key = line.getAttribute('data-line-key');
      if (!key) return;

      setError('');
      var alert = line.querySelector('[data-orchid-cart-line-alert]');
      if (alert) alert.textContent = '';

      var input = line.querySelector('[data-orchid-cart-qty]');
      if (input) input.value = String(quantity);

      setMinusState(line, quantity);
      optimistic(line, quantity);
      line.setAttribute('aria-busy', 'true');
      if (quantity === 0) line.classList.add('is-leaving');

      queue[key] = quantity;
      window.clearTimeout(timers[key]);
      timers[key] = window.setTimeout(run, 350);
    }

    function run() {
      if (running) return;

      var key = null;
      for (var candidate in queue) {
        if (Object.prototype.hasOwnProperty.call(queue, candidate)) {
          key = candidate;
          break;
        }
      }
      if (key === null) return;

      var quantity = queue[key];
      delete queue[key];
      running = true;

      var line = linesEl ? linesEl.querySelector('[data-line-key="' + key + '"]') : null;

      // Prestige's own components ask to be re-rendered by pushing their section id onto this
      // event — it is how ProductForm does it, and the cart drawer registers for it. We have to
      // ask too: the drawer listens for cart:change unconditionally and then reads
      // cart.sections[itsId], so firing that event without having requested its section makes
      // Prestige dereference undefined and throw inside its own replaceContent.
      var bundled = [d.sectionId];
      try {
        var ask = { sections: [] };
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: ask })
        );
        ask.sections.forEach(function (id) {
          if (id && bundled.indexOf(id) === -1) bundled.push(id);
        });
      } catch (error) {
        /* A theme without the drawer simply gets our own section back. */
      }

      fetch(d.changeUrl || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity: quantity,
          sections: bundled.join(','),
          sections_url: d.cartUrl || '/cart'
        })
      })
        .then(function (response) {
          // A blocked or broken response is not always JSON, and a JSON parse error is not a
          // sentence to show a customer. Only Shopify's own `description` is ever surfaced.
          return response
            .json()
            .catch(function () {
              return {};
            })
            .then(function (body) {
              if (!response.ok) {
                var failure = new Error(body && body.description ? body.description : '');
                failure.fromShopify = Boolean(body && body.description);
                throw failure;
              }
              return body;
            });
        })
        .then(function (cart) {
          // An empty cart is a different page, not a shorter list of rows.
          if (cart.item_count === 0) {
            window.location.assign(d.cartUrl || '/cart');
            return;
          }

          // Prestige's <cart-count> in the header listens for exactly this.
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:change', {
              bubbles: true,
              detail: { baseEvent: 'orchid-cart:change', cart: cart }
            })
          );

          var title = line ? line.getAttribute('data-line-title') : '';
          rememberSubtotal(cart);

          // A later change is already queued for another line — leave the optimistic figures
          // alone and let that one's response do the swap, or it would clobber them.
          if (!pendingWork()) {
            adopt(cart);
            // The subtotal carries its own aria-live, so this says what that number cannot —
            // which line moved, and where it landed. Repeating the total here would make a
            // screen reader read the same figure twice for one keypress.
            say(
              quantity === 0
                ? fill(d.labelRemoved, { title: title })
                : fill(d.labelUpdated, {
                    title: title,
                    count: quantity,
                    total: subtotalEl ? subtotalEl.textContent.trim() : ''
                  })
            );
          }
          done();
        })
        .catch(function (error) {
          if (line) {
            revert(line);
            var alert = line.querySelector('[data-orchid-cart-line-alert]');
            if (alert) {
              alert.textContent =
                error && error.fromShopify ? error.message : d.labelError || '';
            }
          }
          // Only shout from the rail when the message could not be put on the line itself.
          if (!line) setError(d.labelError || '');
          done();
        });
    }

    function done() {
      running = false;
      if (pendingWork()) run();
    }

    /* --- Adopting Shopify's own re-render ------------------------------ */

    function adopt(cart) {
      var html = cart && cart.sections ? cart.sections[d.sectionId] : null;
      if (!html) {
        refresh();
        return;
      }

      var parsed;
      try {
        parsed = new DOMParser().parseFromString(html, 'text/html');
      } catch (error) {
        refresh();
        return;
      }

      var freshLines = parsed.querySelector('[data-orchid-cart-lines]');
      if (!freshLines || !linesEl) {
        refresh();
        return;
      }

      var active = document.activeElement;
      var wanted = null;
      if (active && linesEl.contains(active)) {
        var owner = active.closest('[data-orchid-cart-line]');
        wanted = {
          key: owner ? owner.getAttribute('data-line-key') : null,
          step: active.getAttribute('data-orchid-cart-step'),
          remove: active.hasAttribute('data-orchid-cart-remove'),
          qty: active.hasAttribute('data-orchid-cart-qty')
        };
      }

      linesEl.innerHTML = freshLines.innerHTML;

      copyText(parsed, '[data-orchid-cart-count]');
      copyText(parsed, '[data-orchid-cart-subtotal]');
      copyHtml(parsed, '[data-orchid-cart-adjustments]');

      restoreFocus(wanted);
    }

    function copyText(parsed, selector) {
      var fresh = parsed.querySelector(selector);
      var live = root.querySelector(selector);
      if (fresh && live) live.textContent = fresh.textContent;
    }

    function copyHtml(parsed, selector) {
      var fresh = parsed.querySelector(selector);
      var live = root.querySelector(selector);
      if (fresh && live) live.innerHTML = fresh.innerHTML;
    }

    function restoreFocus(wanted) {
      if (!wanted || !linesEl) return;

      var target = null;
      if (wanted.key) {
        var line = linesEl.querySelector('[data-line-key="' + wanted.key + '"]');
        if (line) {
          if (wanted.step) target = line.querySelector('[data-orchid-cart-step="' + wanted.step + '"]');
          else if (wanted.remove) target = line.querySelector('[data-orchid-cart-remove]');
          else if (wanted.qty) target = line.querySelector('[data-orchid-cart-qty]');
        }
      }

      // The row the focus was on is gone — do not drop the user at the top of the document.
      if (!target || target.disabled) target = linesEl;
      try {
        target.focus({ preventScroll: true });
      } catch (error) {
        target.focus();
      }
    }

    function rememberSubtotal(cart) {
      if (subtotalEl && cart && typeof cart.total_price === 'number') {
        subtotalEl.setAttribute('data-cents', String(cart.total_price));
      }
    }

    // Only reached when the write succeeded but Shopify returned no re-rendered section. The
    // cart is right and the page is stale, so reload rather than show numbers we invented.
    function refresh() {
      window.location.reload();
    }

    /* --- Events -------------------------------------------------------- */

    root.addEventListener('click', function (event) {
      var step = event.target.closest ? event.target.closest('[data-orchid-cart-step]') : null;
      if (step) {
        var stepLine = step.closest('[data-orchid-cart-line]');
        if (!stepLine) return;
        var input = stepLine.querySelector('[data-orchid-cart-qty]');
        var current = input ? parseInt(input.value, 10) || 0 : 0;
        var next = current + (parseInt(step.getAttribute('data-orchid-cart-step'), 10) || 0);
        if (next < 0) next = 0;
        if (next === current) return;
        schedule(stepLine, next);
        return;
      }

      var remove = event.target.closest ? event.target.closest('[data-orchid-cart-remove]') : null;
      if (remove) {
        event.preventDefault();
        var removeLine = remove.closest('[data-orchid-cart-line]');
        if (removeLine) schedule(removeLine, 0);
      }
    });

    root.addEventListener('change', function (event) {
      var input = event.target.closest ? event.target.closest('[data-orchid-cart-qty]') : null;
      if (!input) return;
      var line = input.closest('[data-orchid-cart-line]');
      if (!line) return;
      var value = parseInt(input.value, 10);
      if (isNaN(value) || value < 0) value = 0;
      schedule(line, value);
    });

    /* --- The note ------------------------------------------------------ */

    if (noteEl) {
      var noteTimer = null;
      noteEl.addEventListener('change', function () {
        var value = noteEl.value;
        if (noteStatusEl) noteStatusEl.textContent = '';

        fetch(d.updateUrl || '/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note: value })
        })
          .then(function (response) {
            if (!response.ok) throw new Error('note');
            if (noteStatusEl) noteStatusEl.textContent = d.labelNoteSaved || '';
            window.clearTimeout(noteTimer);
            noteTimer = window.setTimeout(function () {
              if (noteStatusEl) noteStatusEl.textContent = '';
            }, 5000);
          })
          .catch(function () {
            // The note is still in the field and still in the form, so it survives to
            // checkout on submit either way. Say what happened rather than pretend.
            if (noteStatusEl) noteStatusEl.textContent = d.labelError || '';
          });
      });
    }

  }

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll(SELECTOR);
    Array.prototype.forEach.call(nodes, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // The theme editor tears a section down and rebuilds it on every setting change.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
